import { MousePointerClick, BarChart3, Layers, AlertTriangle, Gauge, Search } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Google Ads management service page.
 * Positioned as paid search run alongside SEO by a senior consultant,
 * not a standalone PPC agency service.
 */
export const googleAdsPage: MoneyPageContent = {
  id: "google-ads",
  icon: MousePointerClick,
  serviceType: "Google Ads Management",
  esPath: "/servicios/google-ads",
  enPath: "/en/services/google-ads",
  es: {
    metaTitle: "Gestión de Google Ads | Búsqueda pagada estratégica",
    metaDescription:
      "Gestión de Google Ads integrada con SEO: estructura de cuenta, control de gasto desperdiciado y medición real, sin promesas de resultados.",
    keywords: [
      "gestión de google ads",
      "búsqueda pagada",
      "sem",
      "estructura de cuenta google ads",
      "control de gasto google ads",
      "medición de conversiones",
    ],
    h1: "Google Ads gestionado como parte de una estrategia de búsqueda, no como un canal aislado",
    breadcrumbLabel: "Google Ads",
    heroSubtitle: "Capturar demanda que ya existe",
    heroDescription:
      "Google Ads funciona cuando responde a una decisión clara sobre qué demanda capturar, cómo estructurar la cuenta y qué medir. No es subir campañas y esperar. Trabajo la búsqueda pagada junto con SEO, entendiendo dónde cada canal aporta y dónde solo duplica gasto.",
    ctaPrimary: {
      label: "Solicitar diagnóstico estratégico",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos", to: "/casos-de-exito" },
    summary: [
      {
        label: "Criterio de inversión",
        text: "Cuándo tiene sentido pagar por tráfico y cuándo ese presupuesto rinde más en otro lugar.",
      },
      {
        label: "Estructura de cuenta",
        text: "Campañas y grupos de anuncios organizados por intención real, no por catálogo de producto copiado.",
      },
      {
        label: "Control de gasto desperdiciado",
        text: "Términos de búsqueda, ubicaciones y audiencias que consumen presupuesto sin generar valor.",
      },
      {
        label: "Medición honesta",
        text: "Qué se puede atribuir con confianza y qué queda en zona gris entre canales.",
      },
    ],
    explanation: {
      title: "Qué es la gestión de Google Ads (y qué no es)",
      definition:
        "Es la administración estratégica de campañas de búsqueda pagada: decidir qué palabras clave perseguir, cómo estructurar cuentas y campañas, qué mensajes probar y cómo controlar el gasto para que financie conversiones reales. No es la creación aislada de anuncios ni la optimización automática que ofrece la plataforma por defecto.",
      whyImportant: {
        title: "Por qué la estructura decide el resultado antes que la puja",
        text: "Una cuenta mal estructurada castiga cualquier presupuesto, sin importar cuánto se invierta en pujas o creatividades. El orden de campañas, la segmentación de intención y el seguimiento de conversiones determinan si el gasto se convierte en datos útiles o en ruido.",
      },
      consequences: {
        title: "Qué pasa si se gestiona sin criterio",
        text: "Presupuesto consumido por búsquedas irrelevantes, campañas compitiendo entre sí por las mismas palabras, conversiones mal atribuidas y decisiones tomadas sobre datos que no reflejan lo que realmente ocurrió.",
      },
    },
    problems: {
      title: "Problemas frecuentes en cuentas de Google Ads",
      subtitle: "La mayoría no son de puja, son de estructura y medición",
      items: [
        {
          icon: Layers,
          title: "Estructura de cuenta desalineada con la intención",
          description:
            "Campañas organizadas por catálogo o por departamento interno, sin relación con cómo busca realmente el usuario.",
          impact: "Anuncios y páginas de destino que no coinciden con lo que la persona esperaba encontrar.",
        },
        {
          icon: AlertTriangle,
          title: "Gasto desperdiciado en términos irrelevantes",
          description:
            "Ausencia de un trabajo constante de palabras clave negativas y revisión de términos de búsqueda reales.",
          impact: "Presupuesto consumido por clics que nunca iban a convertir.",
        },
        {
          icon: BarChart3,
          title: "Medición de conversiones poco confiable",
          description:
            "Seguimiento configurado una vez y nunca revalidado, o conversiones que mezclan micro y macro eventos sin distinción.",
          impact: "Decisiones de presupuesto basadas en números que no representan el negocio real.",
        },
        {
          icon: Search,
          title: "Superposición entre SEO y Google Ads",
          description:
            "Pagar por clics en palabras donde el sitio ya posiciona de forma orgánica, sin evaluar si eso suma o solo duplica gasto.",
          impact: "Presupuesto pagado ocupando un lugar que el tráfico orgánico ya cubría.",
        },
        {
          icon: Gauge,
          title: "Páginas de destino no alineadas con el anuncio",
          description:
            "Tráfico pagado enviado a páginas genéricas, lentas o sin relación clara con la búsqueda que originó el clic.",
          impact: "Costo por clic alto con una tasa de conversión que no lo justifica.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo la gestión de Google Ads",
      subtitle: "Decisión de canal antes que optimización de campaña",
      steps: [
        {
          number: "01",
          title: "Evaluación de encaje del canal",
          description:
            "Si la búsqueda pagada tiene sentido para el momento del negocio, el margen y la competencia por esas palabras clave, o si el presupuesto rinde mejor en otro lugar.",
        },
        {
          number: "02",
          title: "Estructura de cuenta y segmentación",
          description:
            "Campañas y grupos de anuncios organizados por intención de búsqueda, con mensajes y páginas de destino coherentes entre sí.",
        },
        {
          number: "03",
          title: "Configuración de medición",
          description:
            "Definición de qué conversiones importan realmente al negocio y validación de que el seguimiento las capture con precisión.",
        },
        {
          number: "04",
          title: "Control continuo de gasto",
          description:
            "Revisión periódica de términos de búsqueda, audiencias y ubicaciones para eliminar gasto que no aporta.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Evaluación de si Google Ads tiene sentido para tu situación actual",
          "Estructura de campañas y grupos de anuncios por intención",
          "Configuración y validación de medición de conversiones",
          "Trabajo continuo de palabras clave negativas y control de gasto",
          "Coordinación entre Google Ads y la estrategia de SEO existente",
          "Recomendaciones de página de destino alineadas con cada campaña",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Diseño o desarrollo de páginas de destino como entregable independiente",
          "Gestión de campañas en otras plataformas publicitarias",
          "Promesas de costo por clic o volumen de conversiones",
          "Creación de piezas creativas o producción de video",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Negocios con un margen que permite pagar por adquisición de forma sostenida",
          "Equipos que ya tienen o están construyendo una base de SEO y quieren coordinar ambos canales",
          "Empresas con eventos de conversión claros y medibles",
          "Lanzamientos o temporadas donde el SEO orgánico no puede llegar a tiempo",
        ],
      },
      notFor: {
        title: "Para quién no es el mejor ajuste",
        items: [
          "Negocios sin margen para sostener un costo de adquisición pagado",
          "Quien busca un canal para probar sin definir qué conversión importa",
          "Sitios sin capacidad de recibir y convertir el tráfico que llegue (páginas lentas, checkout roto, etc.)",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿Necesito Google Ads si ya invierto en SEO?",
          answer:
            "No siempre. Depende de la competencia por tus palabras clave, del tiempo que toma posicionar orgánicamente y de si tu margen sostiene un costo por adquisición pagado. Parte del trabajo es justamente evaluar eso antes de recomendar invertir.",
        },
        {
          question: "¿Cuánto presupuesto necesito para empezar?",
          answer:
            "No doy una cifra fija porque depende del costo por clic de tu industria, tu ciclo de venta y tus objetivos. Lo que sí puedo hacer es ayudarte a estimar un rango razonable según tu situación específica.",
        },
        {
          question: "¿Google Ads y SEO compiten por el mismo presupuesto?",
          answer:
            "Compiten por atención y prioridad, no necesariamente por presupuesto. Bien coordinados, cada canal cubre un espacio distinto: SEO construye visibilidad sostenida, Ads captura demanda inmediata. Mal coordinados, terminas pagando por clics que el tráfico orgánico ya te daba.",
        },
        {
          question: "¿Cómo se mide si la inversión en Ads está funcionando?",
          answer:
            "Con conversiones definidas de forma clara desde el inicio, alineadas con lo que realmente genera valor para el negocio, no solo clics o impresiones. La calidad de la medición determina si las decisiones posteriores tienen sentido.",
        },
        {
          question: "¿Trabajas con cuentas que ya existen o solo cuentas nuevas?",
          answer:
            "Ambas. En cuentas existentes empiezo con una revisión de estructura, gasto y medición antes de proponer cambios, para entender qué está funcionando y qué no.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "Google Ads rinde más cuando se coordina con otras áreas",
      links: [
        {
          label: "SEO Técnico",
          description: "Infraestructura del sitio que también afecta la calidad y el costo del tráfico pagado.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "SEO para ecommerce",
          description: "Coordinación entre catálogo orgánico y campañas de shopping o búsqueda.",
          to: "/servicios/ecommerce-seo",
        },
        {
          label: "Auditoría SEO profesional",
          description: "Diagnóstico del estado orgánico antes de decidir cuánto invertir en pagado.",
          to: "/servicios/seo-diagnostico",
        },
      ],
    },
    llmSummary:
      "Servicio de gestión de Google Ads coordinado con SEO: evaluación de encaje del canal, estructura de cuenta por intención, control de gasto desperdiciado y medición de conversiones, sin promesas de resultados.",
    keyPoints: [
      "Evalúa primero si Google Ads tiene sentido antes de gestionar campañas",
      "Estructura de cuenta por intención de búsqueda, no por catálogo",
      "Trabajo continuo de control de gasto y palabras clave negativas",
      "Coordinación explícita con la estrategia de SEO existente",
    ],
  },
  en: {
    metaTitle: "Google Ads Management | Strategic Paid Search",
    metaDescription:
      "Google Ads management coordinated with SEO: account structure, wasted-spend control and honest conversion measurement — no guaranteed results.",
    keywords: [
      "google ads management",
      "paid search",
      "sem strategy",
      "google ads account structure",
      "wasted ad spend",
      "conversion tracking",
    ],
    h1: "Google Ads managed as part of a search strategy, not an isolated channel",
    breadcrumbLabel: "Google Ads",
    heroSubtitle: "Capturing demand that already exists",
    heroDescription:
      "Google Ads works when it's backed by a clear decision about what demand to capture, how to structure the account and what to measure. It's not launching campaigns and hoping. I manage paid search alongside SEO, understanding where each channel adds value and where it just duplicates spend.",
    ctaPrimary: {
      label: "Request a Paid Search Review",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See how I work", to: "/en/case-studies" },
    summary: [
      {
        label: "Investment criteria",
        text: "When it makes sense to pay for traffic, and when that budget performs better elsewhere.",
      },
      {
        label: "Account structure",
        text: "Campaigns and ad groups organized by real search intent, not a copy-pasted product catalog.",
      },
      {
        label: "Wasted-spend control",
        text: "Search terms, placements and audiences that burn budget without generating value.",
      },
      {
        label: "Honest measurement",
        text: "What can be attributed with confidence, and what sits in the gray area between channels.",
      },
    ],
    explanation: {
      title: "What Google Ads management is (and isn't)",
      definition:
        "It's the strategic administration of paid search campaigns: deciding which keywords to pursue, how to structure accounts and campaigns, what messaging to test, and how to control spend so it funds real conversions. It's not writing a few ads in isolation, and it's not letting the platform's default automation run unchecked.",
      whyImportant: {
        title: "Why structure decides the outcome before bidding does",
        text: "A poorly structured account punishes any budget, no matter how much you spend on bids or creative. Campaign organization, intent segmentation and conversion tracking determine whether that spend turns into usable data or just noise.",
      },
      consequences: {
        title: "What happens without a clear approach",
        text: "Budget consumed by irrelevant searches, campaigns competing against each other for the same keywords, conversions attributed incorrectly, and decisions made on data that doesn't reflect what actually happened.",
      },
    },
    problems: {
      title: "Common issues in Google Ads accounts",
      subtitle: "Most aren't bidding problems — they're structure and measurement problems",
      items: [
        {
          icon: Layers,
          title: "Account structure misaligned with intent",
          description:
            "Campaigns organized around a product catalog or internal department, with no connection to how people actually search.",
          impact: "Ads and landing pages that don't match what the searcher expected to find.",
        },
        {
          icon: AlertTriangle,
          title: "Spend wasted on irrelevant terms",
          description:
            "No ongoing negative keyword work and no regular review of the actual search terms triggering ads.",
          impact: "Budget consumed by clicks that were never going to convert.",
        },
        {
          icon: BarChart3,
          title: "Unreliable conversion measurement",
          description:
            "Tracking set up once and never revalidated, or conversions that mix micro and macro events with no distinction.",
          impact: "Budget decisions based on numbers that don't represent the real business.",
        },
        {
          icon: Search,
          title: "Overlap between SEO and Google Ads",
          description:
            "Paying for clicks on keywords the site already ranks for organically, without evaluating whether that adds value or just duplicates spend.",
          impact: "Paid budget occupying a spot organic traffic was already covering.",
        },
        {
          icon: Gauge,
          title: "Landing pages misaligned with the ad",
          description:
            "Paid traffic sent to generic, slow, or loosely related landing pages that don't match the search that triggered the click.",
          impact: "High cost per click with a conversion rate that doesn't justify it.",
        },
      ],
    },
    methodology: {
      title: "How I approach Google Ads management",
      subtitle: "Channel decision before campaign optimization",
      steps: [
        {
          number: "01",
          title: "Channel-fit assessment",
          description:
            "Whether paid search makes sense given your business stage, margin and competition for those keywords, or whether the budget performs better elsewhere.",
        },
        {
          number: "02",
          title: "Account structure and segmentation",
          description:
            "Campaigns and ad groups organized by search intent, with messaging and landing pages that stay consistent with each other.",
        },
        {
          number: "03",
          title: "Measurement setup",
          description:
            "Defining which conversions actually matter to the business and validating that tracking captures them accurately.",
        },
        {
          number: "04",
          title: "Ongoing spend control",
          description:
            "Regular review of search terms, audiences and placements to eliminate spend that isn't contributing.",
        },
      ],
      ctaText: "Request a Paid Search Review",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What this service includes",
        items: [
          "Assessment of whether Google Ads makes sense for your current situation",
          "Campaign and ad group structure organized by intent",
          "Conversion tracking setup and validation",
          "Ongoing negative keyword work and spend control",
          "Coordination between Google Ads and your existing SEO strategy",
          "Landing page recommendations aligned with each campaign",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "Landing page design or development as a standalone deliverable",
          "Management of campaigns on other advertising platforms",
          "Promised cost-per-click or conversion volume",
          "Creative asset production or video creation",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this makes sense for",
        items: [
          "Businesses with margin that can sustainably support paid acquisition",
          "Teams that already have, or are building, an SEO foundation and want the two channels coordinated",
          "Companies with clear, measurable conversion events",
          "Launches or seasonal moments where organic SEO can't move fast enough",
        ],
      },
      notFor: {
        title: "Who this isn't the best fit for",
        items: [
          "Businesses without margin to sustain a paid acquisition cost",
          "Anyone looking to test a channel without defining which conversion matters",
          "Sites that can't yet convert the traffic they'd receive (slow pages, broken checkout, etc.)",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Do I need Google Ads if I'm already investing in SEO?",
          answer:
            "Not always. It depends on competition for your keywords, how long organic ranking will take, and whether your margin supports a paid acquisition cost. Part of this work is evaluating that before recommending you spend anything.",
        },
        {
          question: "How much budget do I need to get started?",
          answer:
            "I don't quote a fixed number because it depends on your industry's cost per click, your sales cycle and your goals. What I can do is help you estimate a reasonable range based on your specific situation.",
        },
        {
          question: "Do Google Ads and SEO compete for the same budget?",
          answer:
            "They compete for attention and priority more than for budget itself. Coordinated well, each channel covers different ground: SEO builds sustained visibility, Ads captures immediate demand. Coordinated poorly, you end up paying for clicks organic traffic was already delivering.",
        },
        {
          question: "How is success measured for an Ads investment?",
          answer:
            "With conversions defined clearly from the start, tied to what actually generates value for the business — not just clicks or impressions. Measurement quality determines whether downstream decisions make any sense.",
        },
        {
          question: "Do you work with existing accounts or only new ones?",
          answer:
            "Both. For existing accounts, I start with a review of structure, spend and measurement before proposing any changes, so I understand what's already working and what isn't.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "Google Ads performs better when coordinated with other areas",
      links: [
        {
          label: "Technical SEO",
          description: "Site infrastructure that also affects the quality and cost of paid traffic.",
          to: "/en/services/technical-seo",
        },
        {
          label: "Ecommerce SEO",
          description: "Coordination between the organic catalog and shopping or search campaigns.",
          to: "/en/services/ecommerce-seo",
        },
        {
          label: "Professional SEO Audit",
          description: "A read on your organic standing before deciding how much to invest in paid.",
          to: "/en/services/seo-audit",
        },
      ],
    },
    llmSummary:
      "Google Ads management service coordinated with SEO: channel-fit assessment, intent-based account structure, wasted-spend control and conversion measurement, with no guaranteed results.",
    keyPoints: [
      "Evaluates whether Google Ads makes sense before managing any campaign",
      "Account structure organized by search intent, not by catalog",
      "Ongoing spend control and negative keyword work",
      "Explicit coordination with the existing SEO strategy",
    ],
  },
};
